import { useState } from 'react';
import { useT } from '@/store';
import { useDrivers } from '@/hooks/useDirectory';
import {
  DRIVER_CHANNELS,
  LICENSE_CATEGORIES,
  useDeleteDriver,
  useResetDriverPassword,
  useRestoreDriver,
  useRotateDriverKey,
  useSaveDriver,
} from '@/hooks/useDriverAdmin';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { Modal } from '@/components/ui/Overlay';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';
import { daysUntil, parseDecimalInput, relativeTime } from '@/lib/format';
import { TelegramQrModal } from './TelegramQrModal';
import { DriverKeyModal } from './DriverKeyModal';
import { SmartImportModal } from '@/components/data/SmartImportModal';

/**
 * The four documents a Lithuanian professional driver cannot legally drive
 * without, and the ones app.py's daily sweep watches. Ordered by how often
 * they actually catch a carrier out rather than alphabetically: the 95 code
 * is first because it is the one that lapses unnoticed — the plastic card
 * looks identical either way, only the code and its date change.
 */
const DOCUMENT_FIELDS = [
  // hintKey is the one difference from the other three: 95 kodas is the
  // document dispatchers most often ask "what is this?" about, since
  // nothing about the physical card changes when it lapses — see the
  // module comment above.
  { field: 'cpc_expiry', labelKey: 'drivers.cpcExpiryLabel', hintKey: 'drivers.cpcExpiryHint' },
  { field: 'medical_expiry', labelKey: 'drivers.medicalExpiryLabel' },
  { field: 'licence_expiry', labelKey: 'drivers.licenceExpiryLabel' },
  { field: 'tacho_card_expiry', labelKey: 'drivers.tachoCardExpiryLabel' },
];

const EMPTY_DOCUMENTS = Object.fromEntries(DOCUMENT_FIELDS.map((d) => [d.field, '']));

/**
 * The document closest to running out, or null when this driver has none
 * entered or none inside the thirty-day window the backend alerts on.
 *
 * The same two thresholds as app.py's sweep, deliberately: a row that turned
 * amber on a date the Telegram alert stayed silent about — or the reverse —
 * would make the dispatcher trust neither.
 *
 * A driver with no dates at all returns null and gets NO badge. Not a grey
 * "not entered" chip: that would put a mark on every driver of every carrier
 * who has not done this data entry yet, and a badge that is always there is
 * read as decoration within a day.
 */
function soonestDocument(driver) {
  let worst = null;
  for (const document of DOCUMENT_FIELDS) {
    const days = daysUntil(driver[document.field]);
    if (days === null || days > 30) continue;
    if (worst === null || days < worst.days) worst = { ...document, days };
  }
  return worst;
}

const EMPTY_FORM = {
  full_name: '',
  phone_number: '',
  vehicle_plate: '',
  vehicle_capacity_tons: '',
  preferred_channel: 'whatsapp',
  // Transporto kainodaros modeliai: per-driver overrides of the tenant's
  // driver_day_rate_eur/driver_commission_percent defaults. Blank means
  // "use the tenant's own rate" — a real, common answer, not a gap.
  day_rate_eur: '',
  commission_percent: '',
  license_categories: [],
  // A driver being created can't have linked Telegram yet, so picking that
  // channel in this form always shows the "not linked" hint below.
  telegram_linked: false,
  ...EMPTY_DOCUMENTS,
};

/**
 * Driver administration.
 *
 * Inactive drivers are shown, greyed, rather than hidden: deleting a driver
 * only deactivates them (their name still has to render on every historical
 * order), so a list that hid them would leave an admin unable to find
 * someone they had just "deleted" by mistake.
 *
 * Two actions here mint an API key that is visible exactly once — creating a
 * driver and rotating a key. Both hand it to a modal the admin has to
 * dismiss deliberately, never a toast that can disappear before it is copied.
 */
export function DriverList() {
  const t = useT();
  const { data: drivers = [], isLoading, isError, error } = useDrivers({ includeInactive: true });
  const saveDriver = useSaveDriver();
  const deleteDriver = useDeleteDriver();
  const restoreDriver = useRestoreDriver();
  const rotateKey = useRotateDriverKey();
  const resetPassword = useResetDriverPassword();

  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrDriver, setQrDriver] = useState(null);
  const [issuedKey, setIssuedKey] = useState(null);

  const submit = () => {
    if (!editing.full_name.trim() || !editing.phone_number.trim()) return;
    // V-351. parseDecimalInput, not Number.parseFloat: all three boxes are
    // inputMode="decimal" text fields, and an lt-locale keyboard puts a
    // COMMA on that decimal key. Number.parseFloat('120,50') is 120 — not
    // NaN, so the Number.isNaN guards below wave it through and the driver
    // is paid fifty cents a day less than the dispatcher typed, for as long
    // as nobody reopens the form.
    const capacity = parseDecimalInput(editing.vehicle_capacity_tons);
    const dayRate = parseDecimalInput(editing.day_rate_eur);
    const commission = parseDecimalInput(editing.commission_percent);
    saveDriver.mutate(
      {
        driverId: editing.driverId,
        full_name: editing.full_name.trim(),
        phone_number: editing.phone_number.trim(),
        vehicle_plate: editing.vehicle_plate.trim() || undefined,
        vehicle_capacity_tons: Number.isNaN(capacity) ? undefined : capacity,
        day_rate_eur: Number.isNaN(dayRate) ? undefined : dayRate,
        commission_percent: Number.isNaN(commission) ? undefined : commission,
        preferred_channel: editing.preferred_channel,
        // null, not undefined, for a field the dispatcher cleared. undefined
        // drops out of the JSON body and the backend leaves the column
        // alone, so a date typed into the wrong row could never be taken
        // back out — and it would be chased every morning until somebody
        // edited the database by hand.
        ...Object.fromEntries(
          DOCUMENT_FIELDS.map(({ field }) => [field, editing[field].trim() || null]),
        ),
        license_categories: editing.license_categories,
      },
      {
        onSuccess: (result) => {
          setEditing(null);
          // Only a create returns credentials; an edit does not. The
          // username and password matter more than the key here: they are
          // what the driver types into the app's sign-in screen, and a
          // create used to return neither, leaving them locked out until a
          // server restart.
          if (result?.api_key) {
            setIssuedKey({
              name: result.full_name,
              apiKey: result.api_key,
              username: result.username,
              password: result.password,
            });
          }
        },
      },
    );
  };

  return (
    <Panel
      title={t('nav.drivers')}
      actions={
        <>
          {/* P1-1: a driver is about six manual actions — create, key, QR,
              Telegram link, channel, vehicle — and a fifteen-truck fleet is
              ninety of them on the installation day. The import issues the
              login credentials too, so an imported driver can actually sign
              in; see blueprints/imports.py's _provision_driver. */}
          <button type="button" className="btn" onClick={() => setImporting(true)}>
            {t('import.openBtn')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing({ ...EMPTY_FORM })}>
            {t('drivers.addDriver')}
          </button>
        </>
      }
    >
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : drivers.length === 0 ? (
        <EmptyState>{t('drivers.noneYet')}</EmptyState>
      ) : (
        <ul className="space-y-2">
          {drivers.map((driver) => (
            <li
              key={driver.driver_id}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-[11px] border border-line p-3 ${
                driver.active ? 'bg-white/2' : 'bg-transparent opacity-55'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold">{driver.full_name}</span>
                  <span className="rounded-full bg-white/6 px-2 py-0.5 text-[9.5px] font-bold uppercase text-dim">
                    {t(`status.driver.${driver.status ?? 'idle'}`)}
                  </span>
                  {driver.telegram_chat_id ? (
                    <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[9.5px] font-bold uppercase text-accent">
                      Telegram
                    </span>
                  ) : null}
                  {(driver.license_categories ?? []).map((category) => (
                    <span
                      key={category}
                      className="rounded-full bg-structure/15 px-2 py-0.5 text-[9.5px] font-bold uppercase text-structure"
                    >
                      {category}
                    </span>
                  ))}
                  {!driver.active ? <ArchivedBadge deletedAt={driver.deleted_at} /> : null}
                  <DocumentBadge driver={driver} t={t} />
                </div>
                <div className="truncate text-[11px] text-dim">
                  {[driver.license_plate, driver.phone_number, driver.username]
                    .filter(Boolean)
                    .join(' · ')}
                  {driver.last_ping_at ? ` · ${relativeTime(driver.last_ping_at, t)}` : ''}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                <button type="button" className="btn !px-2.5 !py-1 !text-[11px]" onClick={() => setQrDriver(driver)}>
                  {t('drivers.telegramQr')}
                </button>
                <ConfirmButton
                  label={t('drivers.resetPassword')}
                  confirmLabel={t('drivers.confirmResetPassword')}
                  className="!text-dim !border-line"
                  onConfirm={() =>
                    resetPassword.mutate(driver.driver_id, {
                      onSuccess: (result) =>
                        setIssuedKey({
                          name: driver.full_name,
                          username: result.username,
                          password: result.password,
                        }),
                    })
                  }
                />
                <ConfirmButton
                  label={t('drivers.rotateKey')}
                  confirmLabel={t('drivers.confirmRotate')}
                  className="!text-dim !border-line"
                  onConfirm={() =>
                    rotateKey.mutate(driver.driver_id, {
                      onSuccess: (result) =>
                        setIssuedKey({ name: driver.full_name, apiKey: result.api_key }),
                    })
                  }
                />
                <button
                  type="button"
                  className="btn !px-2.5 !py-1 !text-[11px]"
                  onClick={() =>
                    setEditing({
                      driverId: driver.driver_id,
                      full_name: driver.full_name ?? '',
                      phone_number: driver.phone_number ?? '',
                      vehicle_plate: driver.vehicle_plate ?? '',
                      vehicle_capacity_tons: String(driver.vehicle_capacity_tons ?? ''),
                      day_rate_eur: String(driver.day_rate_eur ?? ''),
                      commission_percent: String(driver.commission_percent ?? ''),
                      preferred_channel: driver.preferred_channel ?? 'whatsapp',
                      license_categories: driver.license_categories ?? [],
                      telegram_linked: Boolean(driver.telegram_chat_id),
                      ...Object.fromEntries(
                        DOCUMENT_FIELDS.map(({ field }) => [field, driver[field] ?? '']),
                      ),
                    })
                  }
                >
                  {t('common.editBtn')}
                </button>
                {driver.active ? (
                  <ConfirmButton
                    label={t('common.deleteBtn')}
                    confirmLabel={t('common.confirmDelete')}
                    onConfirm={() => deleteDriver.mutate(driver.driver_id)}
                  />
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary !px-2.5 !py-1 !text-[11px]"
                    disabled={restoreDriver.isPending}
                    onClick={() => restoreDriver.mutate(driver.driver_id)}
                  >
                    {t('common.restoreBtn')}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {deleteDriver.isError ? <ErrorState error={deleteDriver.error} /> : null}
      {restoreDriver.isError ? <ErrorState error={restoreDriver.error} /> : null}
      {rotateKey.isError ? <ErrorState error={rotateKey.error} /> : null}
      {resetPassword.isError ? <ErrorState error={resetPassword.error} /> : null}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={t(editing?.driverId ? 'common.editBtn' : 'drivers.addDriver')}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={saveDriver.isPending}>
              {t('common.saveBtn')}
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <Field label={t('drivers.fullNameLabel')}>
              <input
                className="field"
                value={editing.full_name}
                onChange={(e) => setEditing({ ...editing, full_name: e.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('drivers.phoneLabel')}>
                <input
                  className="field"
                  value={editing.phone_number}
                  onChange={(e) => setEditing({ ...editing, phone_number: e.target.value })}
                  placeholder="+3706..."
                />
              </Field>
              <Field label={t('drivers.plateLabel')}>
                <input
                  className="field"
                  value={editing.vehicle_plate}
                  onChange={(e) => setEditing({ ...editing, vehicle_plate: e.target.value })}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('drivers.capacityLabel')}>
                <input
                  className="field"
                  value={editing.vehicle_capacity_tons}
                  onChange={(e) => setEditing({ ...editing, vehicle_capacity_tons: e.target.value })}
                  inputMode="decimal"
                />
              </Field>
              <Field label={t('drivers.channelLabel')}>
                <select
                  className="field"
                  value={editing.preferred_channel}
                  onChange={(e) => setEditing({ ...editing, preferred_channel: e.target.value })}
                >
                  {DRIVER_CHANNELS.map((channel) => (
                    <option key={channel} value={channel}>
                      {channel}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('drivers.dayRateLabel')}>
                <input
                  className="field"
                  value={editing.day_rate_eur}
                  onChange={(e) => setEditing({ ...editing, day_rate_eur: e.target.value })}
                  placeholder={t('drivers.dayRatePh')}
                  inputMode="decimal"
                />
              </Field>
              <Field label={t('drivers.commissionLabel')}>
                <input
                  className="field"
                  value={editing.commission_percent}
                  onChange={(e) => setEditing({ ...editing, commission_percent: e.target.value })}
                  placeholder={t('drivers.commissionPh')}
                  inputMode="decimal"
                />
              </Field>
            </div>
            {/* Not wrapped in the shared Field helper: Field puts its children
                inside one outer <label>, which is fine for a single input but
                would nest several independently-labeled checkboxes inside it
                — invalid markup and a double-toggle risk on click. */}
            <div>
              <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
                {t('drivers.licenseCategoriesLabel')}
              </span>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                {LICENSE_CATEGORIES.map((category) => (
                  <label key={category} className="flex items-center gap-1.5 text-[11.5px]">
                    <input
                      type="checkbox"
                      checked={editing.license_categories.includes(category)}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          license_categories: e.target.checked
                            ? [...editing.license_categories, category]
                            : editing.license_categories.filter((c) => c !== category),
                        })
                      }
                    />
                    {category}
                  </label>
                ))}
              </div>
            </div>
            {/* Choosing Telegram for a driver who never scanned their QR is a
                dead channel: the backend logs the skipped send but nothing
                reaches the cab. Say so here, next to the choice, rather than
                letting the dispatcher find out from an undelivered order. */}
            {editing.preferred_channel === 'telegram' && !editing.telegram_linked ? (
              <p className="text-[10.5px] text-amber">{t('drivers.channelUnlinkedTelegram')}</p>
            ) : null}

            <div className="border-t border-line pt-3">
              <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
                {t('drivers.documentsSection')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {DOCUMENT_FIELDS.map(({ field, labelKey, hintKey }) => (
                  <Field key={field} label={t(labelKey)} hint={hintKey ? t(hintKey) : undefined}>
                    <input
                      className="field"
                      type="date"
                      value={editing[field]}
                      onChange={(e) => setEditing({ ...editing, [field]: e.target.value })}
                    />
                  </Field>
                ))}
              </div>
              {/* Said next to the empty boxes, because four blank date fields
                  read as four things the dispatcher is being told to go and
                  find. They are not: nothing is chased that nobody entered,
                  and the driver can be saved without any of them. */}
              <p className="mt-2 text-[10.5px] text-dim">{t('drivers.documentsHint')}</p>
            </div>
            {saveDriver.isError ? <ErrorState error={saveDriver.error} /> : null}
          </div>
        ) : null}
      </Modal>

      <TelegramQrModal driver={qrDriver} onClose={() => setQrDriver(null)} />
      <DriverKeyModal issued={issuedKey} onClose={() => setIssuedKey(null)} />
      <SmartImportModal
        open={importing}
        onClose={() => setImporting(false)}
        entityType="driver"
        title={t('import.titleDrivers')}
      />
    </Panel>
  );
}

/**
 * The one thing a dispatcher scanning this list needs to see without opening
 * anything: which driver cannot legally be sent out tomorrow.
 *
 * Red at seven days or less (including already expired), amber up to thirty
 * — the same two thresholds the backend alerts on. An expired document says
 * so in words rather than counting days backwards: "-3 d." beside a licence
 * is a number the reader has to decode, and this is the badge that decides
 * whether they hand somebody a load.
 */
function DocumentBadge({ driver, t }) {
  const worst = soonestDocument(driver);
  if (!worst) return null;
  const critical = worst.days <= 7;
  const label = t(worst.labelKey);
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase ${
        critical ? 'bg-red/15 text-red' : 'bg-amber/15 text-amber'
      }`}
    >
      {worst.days < 0
        ? t('drivers.documentExpired', { doc: label })
        : t('drivers.documentExpiring', { doc: label, n: worst.days })}
    </span>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">
        {label}
        {/* Native title attribute rather than a custom tooltip component:
            there isn't one in this codebase yet, and a hover-only badge
            explaining what "95 kodas" is beats the dispatcher having to go
            ask someone or guess. */}
        {hint ? (
          <span
            className="flex h-3.5 w-3.5 shrink-0 cursor-help items-center justify-center rounded-full border border-line text-[9px] font-bold normal-case text-dim"
            title={hint}
          >
            ?
          </span>
        ) : null}
      </span>
      {children}
    </label>
  );
}
